import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {
  WorkflowDTO,
  InputComponentDTO,
  RulesetComponentDTO,
  CreateWorkflowRequest,
  UpdateInputComponentRequest,
  AddRulesetComponentRequest,
  UpdateRulesetComponentRequest,
} from '../types/workflow';
import * as workflowApi from '../api/workflowApi';
import {firstNonEmptyOr} from '../../../core/helpers/nonEmpty';

// Async thunks
export const createWorkflow = createAsyncThunk(
  'workflow/createWorkflow',
  async (data: CreateWorkflowRequest) => {
    const response = await workflowApi.createWorkflow(data);
    return response.data;
  }
);

export const fetchWorkflow = createAsyncThunk(
  'workflow/fetchWorkflow',
  async (workflowId: string) => {
    const response = await workflowApi.getWorkflow(workflowId);
    return response.data;
  }
);

export const fetchInputComponent = createAsyncThunk(
  'workflow/fetchInputComponent',
  async (inputComponentId: string) => {
    const response = await workflowApi.fetchInputComponent(inputComponentId);
    return response.data;
  }
);

export const updateInputComponent = createAsyncThunk(
  'workflow/updateInputComponent',
  async ({
    inputComponentId,
    data,
  }: {
    inputComponentId: string;
    data: UpdateInputComponentRequest;
  }) => {
    const response = await workflowApi.updateInputComponent(
      inputComponentId,
      data
    );
    return response.data;
  }
);

export const addRulesetComponent = createAsyncThunk(
  'workflow/addRulesetComponent',
  async (data: AddRulesetComponentRequest) => {
    const response = await workflowApi.addRulesetComponent(data);
    return response.data;
  }
);

export const updateRulesetComponent = createAsyncThunk(
  'workflow/updateRulesetComponent',
  async ({
    rulesetId,
    data,
  }: {
    rulesetId: string;
    data: UpdateRulesetComponentRequest;
  }) => {
    const response = await workflowApi.updateRulesetComponent(rulesetId, data);
    return response.data;
  }
);

export const deleteRulesetComponent = createAsyncThunk(
  'workflow/deleteRulesetComponent',
  async (rulesetId: string) => {
    const response = await workflowApi.deleteRulesetComponent(rulesetId);
    return {rulesetId, data: response.data};
  }
);

export interface WorkflowState {
  currentWorkflow: WorkflowDTO | null;
  inputComponents: Record<string, InputComponentDTO>;
  rulesetComponents: Record<string, RulesetComponentDTO>;
  loading: boolean;
  error: string | null;
  // UI state
  selectedNodeId: string | null;
  // Component-specific loading states
  componentLoading: Record<string, boolean>;
}

const initialState: WorkflowState = {
  currentWorkflow: null,
  inputComponents: {},
  rulesetComponents: {},
  loading: false,
  error: null,
  selectedNodeId: null,
  componentLoading: {},
};

const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setSelectedNodeId: (state, action: PayloadAction<string | null>) => {
      state.selectedNodeId = action.payload;
    },
    clearWorkflow: (state) => {
      state.currentWorkflow = null;
      state.inputComponents = {};
      state.rulesetComponents = {};
      state.selectedNodeId = null;
    },
    // Optimistic updates for better UX
    updateInputComponentLocal: (
      state,
      action: PayloadAction<{id: string; updates: Partial<InputComponentDTO>}>
    ) => {
      const {id, updates} = action.payload;
      if (state.inputComponents[id]) {
        state.inputComponents[id] = {...state.inputComponents[id], ...updates};
      }
    },
    updateRulesetComponentLocal: (
      state,
      action: PayloadAction<{id: string; updates: Partial<RulesetComponentDTO>}>
    ) => {
      const {id, updates} = action.payload;
      if (state.rulesetComponents[id]) {
        state.rulesetComponents[id] = {
          ...state.rulesetComponents[id],
          ...updates,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Create workflow
      .addCase(createWorkflow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWorkflow.fulfilled, (state, action) => {
        state.loading = false;
        state.currentWorkflow = action.payload;
        // Initialize input component from workflow
        const inputComponent = action.payload.components.find(
          (c) => c.type === 'input'
        );
        if (inputComponent) {
          state.inputComponents[inputComponent.id] = inputComponent;
        }
      })
      .addCase(createWorkflow.rejected, (state, action) => {
        state.loading = false;
        state.error = firstNonEmptyOr(
          'Failed to create workflow',
          action.error.message
        );
      })

      // Fetch workflow
      .addCase(fetchWorkflow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflow.fulfilled, (state, action) => {
        state.loading = false;
        state.currentWorkflow = action.payload;
        // Populate component caches
        action.payload.components.forEach((component) => {
          if (component.type === 'input') {
            state.inputComponents[component.id] = component;
          } else if (component.type === 'ruleset') {
            state.rulesetComponents[component.id] = component;
          }
        });
      })
      .addCase(fetchWorkflow.rejected, (state, action) => {
        state.loading = false;
        state.error = firstNonEmptyOr(
          'Failed to fetch workflow',
          action.error.message
        );
      })

      // Fetch input component
      .addCase(fetchInputComponent.pending, (state, action) => {
        state.componentLoading[action.meta.arg] = true;
      })
      .addCase(fetchInputComponent.fulfilled, (state, action) => {
        state.inputComponents[action.payload.id] = action.payload;
        delete state.componentLoading[action.meta.arg];
      })
      .addCase(fetchInputComponent.rejected, (state, action) => {
        delete state.componentLoading[action.meta.arg];
        state.error = firstNonEmptyOr(
          'Failed to fetch input component',
          action.error.message
        );
      })

      // Update input component
      .addCase(updateInputComponent.pending, (state, action) => {
        state.componentLoading[action.meta.arg.inputComponentId] = true;
      })
      .addCase(updateInputComponent.fulfilled, (state, action) => {
        state.inputComponents[action.payload.id] = action.payload;
        delete state.componentLoading[action.meta.arg.inputComponentId];
      })
      .addCase(updateInputComponent.rejected, (state, action) => {
        delete state.componentLoading[action.meta.arg.inputComponentId];
        state.error = firstNonEmptyOr(
          'Failed to update input component',
          action.error.message
        );
      })

      // Add ruleset component
      .addCase(addRulesetComponent.pending, (state) => {
        state.loading = true;
      })
      .addCase(addRulesetComponent.fulfilled, (state, action) => {
        state.loading = false;
        state.rulesetComponents[action.payload.id] = action.payload;
      })
      .addCase(addRulesetComponent.rejected, (state, action) => {
        state.loading = false;
        state.error = firstNonEmptyOr(
          'Failed to add ruleset component',
          action.error.message
        );
      })

      // Update ruleset component
      .addCase(updateRulesetComponent.pending, (state, action) => {
        state.componentLoading[action.meta.arg.rulesetId] = true;
      })
      .addCase(updateRulesetComponent.fulfilled, (state, action) => {
        state.rulesetComponents[action.payload.id] = action.payload;
        delete state.componentLoading[action.meta.arg.rulesetId];
      })
      .addCase(updateRulesetComponent.rejected, (state, action) => {
        delete state.componentLoading[action.meta.arg.rulesetId];
        state.error = firstNonEmptyOr(
          'Failed to update ruleset component',
          action.error.message
        );
      })

      // Delete ruleset component
      .addCase(deleteRulesetComponent.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteRulesetComponent.fulfilled, (state, action) => {
        state.loading = false;
        delete state.rulesetComponents[action.payload.rulesetId];
      })
      .addCase(deleteRulesetComponent.rejected, (state, action) => {
        state.loading = false;
        state.error = firstNonEmptyOr(
          'Failed to delete ruleset component',
          action.error.message
        );
      });
  },
});

export const {
  clearError,
  setError,
  setSelectedNodeId,
  clearWorkflow,
  updateInputComponentLocal,
  updateRulesetComponentLocal,
} = workflowSlice.actions;

export default workflowSlice.reducer;
